import React, { useState, useEffect } from 'react';
import { Info, ChevronDown, ChevronUp, Copy, Check } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { messagesAPI } from '../services/api';

const MessageVariables = ({ onInsertVariable, textareaRef, ocultar = [] }) => {
  const { darkMode } = useTheme();
  const [showHelp, setShowHelp] = useState(false);
  const [variables, setVariables] = useState([]);
  const [copiedVar, setCopiedVar] = useState(null);

  useEffect(() => {
    loadVariables();
  }, []);

  const loadVariables = async () => {
    try {
      const response = await messagesAPI.getVariables();
      setVariables(response.data.variaveis || []);
    } catch (error) {
      console.error('Erro ao carregar variáveis:', error);
      // Fallback com variáveis padrão
      setVariables([
        { chave: 'nome', descricao: 'Nome completo do contato', exemplo: 'João Silva' },
        { chave: 'primeiro_nome', descricao: 'Primeiro nome do contato', exemplo: 'João' },
        { chave: 'telefone', descricao: 'Telefone do contato', exemplo: '11999999999' },
        { chave: 'data', descricao: 'Data atual', exemplo: '14/10/2025' },
        { chave: 'hora', descricao: 'Hora atual', exemplo: '14:30' },
      ]);
    }
  };

  const insertVariable = (varKey) => {
    const variableText = `{{${varKey}}}`;
    
    if (textareaRef && textareaRef.current) {
      const textarea = textareaRef.current;
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = textarea.value;
      const before = text.substring(0, start);
      const after = text.substring(end, text.length);
      
      const newText = before + variableText + after;
      const newCursorPos = start + variableText.length;
      
      // Atualizar o valor
      if (onInsertVariable) {
        onInsertVariable(newText, newCursorPos);
      }
      
      // Focar no textarea e posicionar cursor
      setTimeout(() => {
        textarea.focus();
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    }
  };

  const copyVariable = (varKey) => {
    const variableText = `{{${varKey}}}`;
    navigator.clipboard.writeText(variableText);
    setCopiedVar(varKey);
    setTimeout(() => setCopiedVar(null), 2000);
  };

  return (
    <div className={`rounded-lg border ${darkMode ? 'border-gray-700 bg-zinc-800' : 'border-gray-300 bg-white'}`}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setShowHelp(!showHelp)}
        className={`w-full flex items-center justify-between p-3 rounded-t-lg transition-colors ${
          darkMode 
            ? 'hover:bg-zinc-700 text-gray-300' 
            : 'hover:bg-gray-50 text-gray-700'
        }`}
      >
        <div className="flex items-center space-x-2">
          <Info className="h-4 w-4 text-blue-500" />
          <span className="text-sm font-medium">
            Variáveis Personalizadas
          </span>
        </div>
        {showHelp ? (
          <ChevronUp className="h-4 w-4" />
        ) : (
          <ChevronDown className="h-4 w-4" />
        )}
      </button>

      {/* Content */}
      {showHelp && (
        <div className={`p-4 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
          <div className={`mb-3 p-3 rounded-md ${darkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
            <p className={`text-sm ${darkMode ? 'text-blue-300' : 'text-blue-800'}`}>
              <strong>💡 Como usar:</strong> Clique em uma variável abaixo para inseri-la no texto da mensagem. 
              Ela será automaticamente substituída pelo valor real ao enviar.
            </p>
          </div>

          <div className="space-y-2">
            <h4 className={`text-sm font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Variáveis Disponíveis:
            </h4>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {variables.filter((v) => !ocultar.includes(v.chave)).map((variable) => (
                <div
                  key={variable.chave}
                  className={`p-3 rounded-md border transition-colors ${
                    darkMode 
                      ? 'border-gray-700 bg-zinc-700 hover:bg-zinc-600' 
                      : 'border-gray-200 bg-gray-50 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2 mb-1">
                        <code className={`text-sm font-mono px-2 py-0.5 rounded ${
                          darkMode ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                        }`}>
                          {`{{${variable.chave}}}`}
                        </code>
                      </div>
                      <p className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {variable.descricao}
                      </p>
                      <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                        Ex: <span className="font-medium">{variable.exemplo}</span>
                      </p>
                    </div>
                    
                    <div className="flex flex-col space-y-1 ml-2">
                      <button
                        type="button"
                        onClick={() => insertVariable(variable.chave)}
                        className={`p-1.5 rounded text-xs transition-colors ${
                          darkMode 
                            ? 'bg-green-600 hover:bg-green-700 text-white' 
                            : 'bg-green-500 hover:bg-green-600 text-white'
                        }`}
                        title="Inserir no texto"
                      >
                        Inserir
                      </button>
                      
                      <button
                        type="button"
                        onClick={() => copyVariable(variable.chave)}
                        className={`p-1.5 rounded transition-colors ${
                          darkMode 
                            ? 'bg-zinc-600 hover:bg-zinc-500 text-gray-300' 
                            : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                        }`}
                        title="Copiar variável"
                      >
                        {copiedVar === variable.chave ? (
                          <Check className="h-3 w-3" />
                        ) : (
                          <Copy className="h-3 w-3" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={`mt-4 p-3 rounded-md ${darkMode ? 'bg-yellow-900/20 border border-yellow-800' : 'bg-yellow-50 border border-yellow-200'}`}>
            <p className={`text-xs ${darkMode ? 'text-yellow-300' : 'text-yellow-800'}`}>
              <strong>⚠️ Importante:</strong> As variáveis só funcionam quando você tem informações do contato. 
              Ao enviar para um número sem cadastro, algumas variáveis podem não ser substituídas.
            </p>
          </div>

          <div className={`mt-3 p-3 rounded-md ${darkMode ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <p className={`text-xs font-semibold mb-2 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              📝 Exemplo de mensagem:
            </p>
            <code className={`text-xs block p-2 rounded ${darkMode ? 'bg-zinc-900 text-gray-300' : 'bg-white text-gray-800'}`}>
              Olá {`{{primeiro_nome}}`}! 👋<br/>
              <br/>
              Tudo bem? Hoje é {`{{dia_semana}}`} e tenho uma novidade especial para você!<br/>
              <br/>
              Qualquer dúvida, estou à disposição.<br/>
              <br/>
              Atenciosamente,<br/>
              {`{{usuario_nome}}`}
            </code>
          </div>
        </div>
      )}
    </div>
  );
};

export default MessageVariables;
